import classNames from "classnames";
import { useMotionValueEvent, useScroll } from "framer-motion";
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useModalControl } from "../../customeHooks/useModalControl";
import { useWebsiteButton } from "../../customeHooks/useWebsiteButtons";
import { authSliceActions } from "../../redux/features/auth/auth.slice";
import { useAppDispatch } from "../../redux/hooks";
import { clientAuthTokenCookieKey } from "../../utils/constants";
import { getCookie } from "../../utils/helpers";
import { Icon } from "../icon/icon";
import { MobileNavModal } from "../modal/app_modals/mobileNavModal";
import { ScrollToTop } from "../scrollToTop";
import { ProfileDropdown } from "./components/app/profile";
import { WebsiteFooter } from "./websiteFooter";

const navs = [
  {
    title: "Home",
    to: "/",
  },
  {
    title: "Find Events",
    to: "/find-events",
  },
  {
    title: "Blog",
    to: "/blog",
  },
  {
    title: "Pricing",
    to: "/pricing",
  },
];

export const Layout = ({
  children,
  footerClassName = "",
  hideFooter = false,
  navShadowOnPageScroll = true,
}: {
  children: (props: {
    isSignedIn: boolean;
    navbarHeight: number;
    pageScrollProgress: number;
    mobileSearchOpen: boolean;
    toggleMobileSearch: () => void;
  }) => ReactNode;
  footerClassName?: string;
  hideFooter?: boolean;
  navShadowOnPageScroll?: boolean;
}) => {
  const [navOpen, setNavOpen] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(
    !!getCookie(clientAuthTokenCookieKey),
  );
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isFloating, setIsFloating] = useState(false);
  const [navbarHeight, setNavbarHeight] = useState(0);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const pageRef = useRef<HTMLDivElement>(null);
  const navbarRef = useRef<HTMLDivElement>(null);

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { scrollYProgress, scrollY } = useScroll();

  useMotionValueEvent(scrollYProgress, "change", (val) => {
    setScrollProgress(val);
  });

  useMotionValueEvent(scrollY, "change", (val) => {
    setIsFloating(val > 24);
  });

  const { goToLogin, goToDashboard } = useWebsiteButton();

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const dispatch = useAppDispatch();

  const dropDownOptions = [
    {
      icon: "share",
      label: "Go to Dashboard",
      onClick: () => {
        goToDashboard();
      },
    },
    {
      icon: "logout",
      label: "Logout",
      onClick: () => {
        dispatch(authSliceActions.signUserOut());

        setIsSignedIn(false);
      },
    },
  ];

  const mobileDropDownOptions = [
    dropDownOptions[0],
    {
      icon: "search",
      label: "Find Events",
      onClick: () => {
        navigate("/find-events");
      },
    },
    {
      icon: "book-open",
      label: "Blog",
      onClick: () => {
        navigate("/blog");
      },
    },
    {
      icon: "coins",
      label: "Pricing",
      onClick: () => {
        navigate("/pricing");
      },
    },
    dropDownOptions[1],
  ];

  useEffect(() => {
    if (!isSignedIn) {
      dispatch(authSliceActions.signUserOut());
    }
    // eslint-disable-next-line
  }, [isSignedIn]);

  useLayoutEffect(() => {
    const node = navbarRef.current;

    if (!node) return;

    const updateHeight = () => setNavbarHeight(node.offsetHeight);

    updateHeight();

    // Recalculate whenever the navbar's own size changes, e.g. as it
    // transitions between its docked and floating states on scroll.
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <ScrollToTop changeKey={pathname} rerunOnKeyChange scrollIntoView />
      <div className="flex min-h-screen flex-col bg-[#F4F7FA]" ref={pageRef}>
        <div id="pageTopRefToScroll" className="h-0" />
        <div
          className={classNames(
            "sticky top-0 !z-[998] w-full bg-transparent transition-all duration-500 ease-in-out",
            {
              "pt-3": isFloating && navShadowOnPageScroll && !navOpen,
            },
          )}
          ref={navbarRef}
        >
          <div
            className={classNames(
              "mx-auto flex flex-col overflow-hidden transition-all duration-500 ease-in-out",
              navOpen
                ? "max-width-website h-screen max-h-[100dvh] overflow-y-scroll bg-white"
                : isFloating && navShadowOnPageScroll
                  ? "w-[calc(100%-32px)] max-w-[800px] rounded-full bg-white shadow-[0px_1px_2px_0px_rgba(6,14,53,0.08),0px_12px_12px_0px_rgba(6,14,53,0.04)]"
                  : "max-width-website",
            )}
          >
            <div
              className={classNames(
                "flex items-center gap-8 transition-all duration-500 ease-in-out",
                !navOpen && isFloating && navShadowOnPageScroll
                  ? "py-3 pl-5 pr-3"
                  : "py-5",
              )}
            >
              <Link to={"/"} className="w-[88px] shrink-0">
                <img
                  src="/assets/logo_full.svg"
                  className="w-full"
                  alt="logo"
                />
              </Link>

              <div className="text-md hidden items-center gap-2 font-onest text-ink/50 lg:flex lg:flex-1">
                {navs.map((nav, idx) => {
                  return (
                    <NavLink
                      to={nav.to}
                      key={`${idx}`}
                      className={({ isActive }) => {
                        return `${isActive ? "text-ink" : "hover:text-ink"} rounded-full px-2.5 py-1.5 transition-all duration-300`;
                      }}
                    >
                      {nav.title}
                    </NavLink>
                  );
                })}
              </div>

              <div className="ml-auto flex shrink-0 items-center gap-3">
                <div
                  className={classNames("hidden", {
                    "lg:!block": isSignedIn,
                  })}
                >
                  <ProfileDropdown
                    dropDownOptions={dropDownOptions}
                    useCookieProfile
                  />
                </div>

                <div
                  className={`hidden items-center gap-3 ${!isSignedIn && "lg:flex"}`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      goToLogin();
                    }}
                    className="rounded-full bg-ink px-5 py-2.5 font-onest text-base font-medium text-white transition-transform duration-300 hover:-translate-y-0.5"
                  >
                    Log In
                  </button>
                </div>

                {pathname === "/find-events" ? (
                  <button
                    type="button"
                    aria-label="Search events"
                    className="w-fit lg:hidden"
                    onClick={() => {
                      setMobileSearchOpen(!mobileSearchOpen);
                    }}
                  >
                    <Icon svg="search" width={20} height={20} />
                  </button>
                ) : undefined}

                <button
                  type="button"
                  className="w-fit lg:hidden"
                  onClick={() => {
                    if (isSignedIn) {
                      handleModalOpen("mobileNavModal");
                    } else {
                      setNavOpen(!navOpen);
                    }
                  }}
                >
                  <Icon
                    svg={navOpen ? "close-icon" : "hamburger"}
                    width={20}
                    height={20}
                    className="transition-all duration-500"
                  />
                </button>
              </div>
            </div>

            {navOpen ? (
              <div className="relative flex flex-col gap-3 py-6 marker:flex-1">
                <img
                  src="/assets/images/sparkles.png"
                  alt="sparkles"
                  className="absolute bottom-[-10px] left-[-20px] max-w-[32px]"
                />
                <img
                  src="/assets/images/ticket.png"
                  alt="sparkles"
                  className="absolute right-0 top-0 max-w-[32px]"
                />
                {navs.map((nav, idx) => {
                  return (
                    <NavLink
                      to={nav.to}
                      key={`${idx}`}
                      className={({ isActive }) => {
                        return `max-w-fit font-onest text-ink/70 ${isActive ? "bg-white text-ink" : ""} rounded-full px-3 py-2.5 transition-all duration-300`;
                      }}
                      onClick={() => {
                        setNavOpen(false);
                      }}
                    >
                      {nav.title}
                    </NavLink>
                  );
                })}

                <div className="mt-[44px] flex flex-col items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      goToLogin();
                    }}
                    className="rounded-full bg-ink px-5 py-2.5 font-onest text-base font-medium text-white"
                  >
                    Log In
                  </button>
                </div>
              </div>
            ) : undefined}
          </div>
        </div>

        <div className="relative w-full flex-1">
          {children({
            isSignedIn,
            navbarHeight,
            pageScrollProgress: scrollProgress,
            mobileSearchOpen,
            toggleMobileSearch: () => setMobileSearchOpen(!mobileSearchOpen),
          })}
        </div>

        {!hideFooter ? (
          <WebsiteFooter className={footerClassName} />
        ) : undefined}
      </div>

      <MobileNavModal
        open={modalOpen && modal === "mobileNavModal"}
        handleClose={handleModalClose}
        options={mobileDropDownOptions}
        useCookieProfile
      />
    </>
  );
};
